import React, { useEffect, useState, useRef } from 'react';
import cx from 'classnames';
import Box from '../../components/boxes/box/box';
import Carousel from '../../components/navigation/carousel/carousel';
import { Button } from '../../core/button/button';
import { Grid, GridItem } from '../../';
import CoverageLabel, { Coverage } from './coverage-label';
import LoadingDots from '../../components/attention-elements/loading-dots/loading-dots';

type PackageSelectorProps = {
    /**
     * List of packages
     */
    packages: PackageProps[];
    /**
     * Default selected package index
     */
    defaultSelectedIndex?: number;
    /**
     * Set loadingPrice true when you are want to show loading sign in place of package price
     */
    loadingPrice?: boolean;
    /**
     * Unique Coverage list for label and info
     */
    uniqueCoverageList: Coverage[];
    /**
     * Mileage selector
     */
    action?: React.ReactNode;
    /**
     * Callback when selected package changes
     * @param packageIndex index of selected package
     * @returns void
     */
    onChangeHandler?: (packageIndex: string) => void;
    /**
     * Callback when click on `Vaelg` button
     * @param packageIndex index of selected package
     * @returns void
     */
    onSubmitHandler?: (packageIndex: string) => void;
} & Omit<React.ComponentPropsWithoutRef<'div'>, 'ref'>;

/**
 * @typedef {Object} PackageProps
 * @property {string} name - Package name
 * @property {string} price - Package Price with currency
 * @property {React.ReactNode} [helpBlock] - Help text to assist in package selection. @deprecated This property is deprecated, use with caution.
 * @property {string[]} coveredCoverageIds - List of covered Coverage id list
 */
type PackageProps = {
    name: string;
    price: string;
    helpBlock?: React.ReactNode;
    coveredCoverageIds: string[];
};

type GridSpan = {
    info: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    details: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    start: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
};

const calcSpan = (len: number): GridSpan => {
    if (len === 1) {
        return { info: 3, details: 3, start: 4 };
    }
    if (len === 2) {
        return { info: 3, details: 4, start: 1 };
    }
    return { info: 3, details: 3, start: 1 };
};

const CoverageDetails = ({ coverages, coverageDetailsList }: any) => {
    return (
        <ul className="List List--icons">
            {coverageDetailsList.map((coverageDetails: Coverage, index: number) => {
                const data_index = coverages.findIndex((coverage: string) => coverage === coverageDetails.id);
                if (data_index === -1) {
                    return <CoverageLabel variant="not-covered" name={coverageDetails.name} info={coverageDetails.info} key={index} />;
                } else {
                    return (
                        <CoverageLabel
                            variant="covered"
                            name={coverageDetails.name}
                            info={coverageDetails.info}
                            gift={coverageDetails.gift}
                            shape={coverageDetails.shape}
                            key={index}
                        />
                    );
                }
            })}
        </ul>
    );
};

const PackageCarousel = ({ packages, selectedPackageIndex, loadingPrice, coverageDetailsList, onPackageChange, onPackageSubmit }: any) => {
    const [selectedIndex, setSelectedIndex] = useState<number>(selectedPackageIndex);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const gridSpan = calcSpan(packages.length);

    useEffect(() => {
        setSelectedIndex(selectedPackageIndex < packages.length ? selectedPackageIndex : 0);
    }, [selectedPackageIndex]);

    const carouselItems = packages.map((package_data: PackageProps, index: number) => {
        const isActive = selectedIndex === index;
        return (
            <GridItem span={gridSpan.details} className={'flos-package-details'} key={index}>
                <Box
                    className={cx('flos-package-coverage', { 'active-package': isActive })}
                    id={`flos-package-coverage-box-${index}`}
                    onClick={() => {
                        setSelectedIndex(index);
                        onPackageChange(index);
                    }}
                >
                    <div className="flos-package-coverage-action">
                        <div className="flos-package-coverage-helpblock">{package_data.helpBlock}</div>
                        <span className="flos-package-coverage-radio">
                            <input type="radio" id={`package-selector-${index}`} className="visuallyhidden" checked={isActive} readOnly />
                            <label htmlFor={`package-selector-${index}`} className={'flos-radio'}>
                                <div className="dot" />
                            </label>
                        </span>
                        <span>
                            <h6 className="u-spacing-stack-s">{package_data.name}</h6>
                            <span className="flos-package-price">{loadingPrice ? <LoadingDots /> : <h3 className={'u-spacing-stack-s'}>{package_data.price}</h3>}</span>
                        </span>
                    </div>
                    <CoverageDetails coverages={package_data.coveredCoverageIds} coverageDetailsList={coverageDetailsList} />
                    {isActive && onPackageSubmit && (
                        <Button
                            className=""
                            theme="primary"
                            onClick={(event) => {
                                event.stopPropagation();
                                onPackageSubmit(index);
                            }}
                        >
                            Vælg
                        </Button>
                    )}
                </Box>
            </GridItem>
        );
    });
    return (
        <Carousel defaultIndex={selectedIndex} ref={containerRef}>
            {carouselItems}
        </Carousel>
    );
};

const PackageSelector = ({
    packages,
    defaultSelectedIndex = 0,
    loadingPrice = false,
    uniqueCoverageList,
    action,
    className,
    onChangeHandler,
    onSubmitHandler,
}: PackageSelectorProps) => {
    const onPackageChange = (packageId: string) => {
        onChangeHandler && onChangeHandler(packageId);
    };
    const onPackageSubmit = (packageId: string) => {
        onSubmitHandler && onSubmitHandler(packageId);
    };
    const gridSpan = calcSpan(packages.length);

    return (
        <div className={cx('flos-package', className)}>
            <Grid gap={7} className={'flos-package-grid-container'}>
                <GridItem className={cx(!action && 'flos-package-info-hide')} span={gridSpan.info} style={{ gridColumnStart: gridSpan.start }}>
                    <div className={cx('flos-package-info', !action && 'flos-package-info-no-action')}>
                        <div className="flos-package-info-action">{action}</div>
                        <div className="flos-package-info-legends">
                            <ul className="List">
                                {uniqueCoverageList.map((coverageDetails: Coverage, index: number) => (
                                    <CoverageLabel
                                        name={coverageDetails.name}
                                        info={coverageDetails.info}
                                        variant={coverageDetails.variant}
                                        gift={coverageDetails.gift}
                                        key={index}
                                    />
                                ))}
                            </ul>
                        </div>
                    </div>
                </GridItem>
                {packages && (
                    <PackageCarousel
                        packages={packages}
                        loadingPrice={loadingPrice}
                        selectedPackageIndex={defaultSelectedIndex}
                        coverageDetailsList={uniqueCoverageList}
                        onPackageChange={onPackageChange}
                        onPackageSubmit={onSubmitHandler && onPackageSubmit}
                    />
                )}
            </Grid>
        </div>
    );
};

export type { PackageSelectorProps, PackageProps };
export { PackageSelector };
export default PackageSelector;
